import React, { useCallback, useState } from "react";
import { PrivacyCenterNoticeDto } from "../../../../../common/service/server/dto/PrivacyCenterDto";
import {
  DataRetentionModule,
  UseDataRetentionModule,
} from "../../../../components/organisms/data-retention/DataRetentionModule";
import { ButtonGroup, ExpandablePanel, Modal, SectionEditor } from "@truevault/aquilo";
import { Form } from "react-final-form";
import { useUpdateNoticeApi } from "../../../../components/privacyCenter/EditPrivacyCenter";
import _ from "lodash";
import { CircularProgress } from "@mui/material";

// The `loading` state is because when switching between editor types we send an API request to save the selected type of editor, so show a loading state while that's happening
type RetentionPolicyEditorType = "builder" | "add-your-own" | "loading";

export function RetentionPolicyEditor({
  organizationId,
  privacyCenterId,
  refreshNotice,
  draftNotice,
  dataRetentionModule,
  showHelpModal,
  setShowHelpModal,
}: {
  organizationId: string;
  privacyCenterId: string;
  refreshNotice: () => Promise<void>;
  draftNotice: PrivacyCenterNoticeDto;
  dataRetentionModule: UseDataRetentionModule;
  showHelpModal: boolean;
  setShowHelpModal: (show: boolean) => void;
}) {
  const [editorType, setEditorType] = useState<RetentionPolicyEditorType>(
    draftNotice.useCustomRetentionPolicySection ? "add-your-own" : "builder",
  );

  const dataRetentionSection = draftNotice.sections.find(
    (s) => s.anchor === "data-retention-policy",
  );
  const [updatedContent, setUpdatedContent] = useState<string>(dataRetentionSection.content);
  const { updateNotice } = useUpdateNoticeApi();

  const debouncedSave = useCallback(
    _.debounce(async (newContent: string) => {
      let updateNoticeBody = {
        organizationId: organizationId,
        privacyCenterId: privacyCenterId,
        noticeId: draftNotice.publicId,
        updateNoticeBody: {
          sections: [{ id: dataRetentionSection.id, content: newContent }],
        },
      };

      await updateNotice(updateNoticeBody);

      await refreshNotice();
    }, 750),
    [],
  );

  const handleSectionChange = useCallback(
    (newContent: string) => {
      setUpdatedContent(newContent);
      debouncedSave(newContent);
    },
    [setUpdatedContent],
  );

  return (
    <div style={{ maxWidth: "764px" }}>
      <div className="tw-boundary">
        <ExpandablePanel
          title="What is a Data Retention Policy?"
          titleFontSize="base"
          widthClass="w-full"
        >
          <p className="text-gray-500 mt-6">
            Data privacy laws require businesses to specify for how long they retain consumer data.
            A Data Retention Policy helps businesses accomplish this by dividing up the data it
            collects into categories. (e.g., "Data we collect in connection with privacy requests")
            and then defining the maximum amount of time it intends to keep that data.
          </p>
          <p className="text-gray-500 mt-6">
            For more information, check out:{" "}
            <a
              className="text-blue-600 underline"
              href="https://knowledge.truevault.com/article/196-create-a-data-retention-policy"
            >
              How to Create a Data Retention Policy
            </a>
          </p>
          <p className="text-primary-600 mt-6 font-medium">
            This policy will not automate deletion of the consumer's data - your company will have
            to enforce this.
          </p>
        </ExpandablePanel>
        <div className="mt-6 text-neutral-800 font-medium" style={{ marginBottom: "12px" }}>
          You are currently using:
        </div>
        <ButtonGroup
          selectedIdx={editorType === "builder" ? 0 : 1}
          onClick={async (idx) => {
            let newEditorType: RetentionPolicyEditorType = idx === 0 ? "builder" : "add-your-own";
            setEditorType("loading");
            await updateNotice({
              organizationId: organizationId,
              privacyCenterId: privacyCenterId,
              noticeId: draftNotice.publicId,
              updateNoticeBody: {
                useCustomRetentionPolicySection: newEditorType === "add-your-own",
              },
            });
            setEditorType(newEditorType);
          }}
        />
      </div>

      {editorType === "loading" && <CircularProgress size="32px" />}

      {editorType === "builder" && <DataRetentionModule {...dataRetentionModule.props} />}

      {editorType === "add-your-own" && (
        <div style={{ marginTop: "24px" }}>
          <Form
            onSubmit={() => {}}
            render={(_formRenderProps) => (
              <SectionEditor
                hideNavigationCheckbox={true}
                hideSideControls={true}
                addToNavigation={false}
                header={dataRetentionSection.header}
                content={updatedContent}
                locked={false}
                placeholder={dataRetentionSection.placeholder}
                onPositionUp={() => {}}
                onPositionDown={() => {}}
                moveDisabled={true}
                moveUpDisabled={true}
                moveDownDisabled={true}
                deleteDisabled={true}
                onShowInNavigationCheckboxToggle={() => {}}
                onDelete={() => {}}
                onInputChange={({ content }) => {
                  handleSectionChange(content);
                }}
                onUnlock={() => {}}
                enablePrivacyCenterToolbar={true}
              />
            )}
          />
        </div>
      )}

      {showHelpModal && (
        <Modal
          title="Add your Retention Policy"
          firstButton="Got it"
          firstButtonColor="primary"
          alignButton="center"
          widthButton="full"
          maxPanelWidth="max-w-lg"
          showOverlay={true}
          closeButton={true}
          closeOnOverlayClick={true}
          onClose={() => setShowHelpModal(false)}
        >
          <p className="text-gray-500">
            Use our Retention Policy Builder or add your own. Your retention policy will show on
            your privacy policy website under your US State Notices and will let your consumers know
            how long you keep their data and for what purpose.
          </p>
          <img
            src="/assets/images/stay_compliant/privacy_center/notices-generation-complete-person-with-checkbox.svg"
            className="mx-auto mt-4"
          />
        </Modal>
      )}
    </div>
  );
}
